import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _dec2, _dec3, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { View } from '@instructure/ui-view';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { childrenOrValue } from '@instructure/ui-prop-types';
import { deprecated, getElementType, omitProps } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
var styles = {
  componentId: 'emyav',
  template: function template(theme) {
    return "\n\n.emyav_bGBk{-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;font-family:".concat(theme.fontFamily || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";margin:0;text-rendering:optimizeLegibility}\n\ninput.emyav_bGBk[type]{-moz-appearance:none;-webkit-appearance:none;appearance:none;background:none;border:none;border-radius:0;box-shadow:none;box-sizing:border-box;color:inherit;display:block;height:auto;line-height:inherit;margin:-6px 0 0 0;outline:0;padding:0;text-align:start;width:100%}\n\n[dir=ltr] input.emyav_bGBk[type]{text-align:left}\n\n[dir=rtl] input.emyav_bGBk[type]{text-align:right}\n\ninput.emyav_bGBk[type]:focus{outline:none}\n\n.emyav_fIqS{font-size:").concat(theme.h1FontSize || 'inherit', ";font-weight:").concat(theme.h1FontWeight || 'inherit', "}\n\n.emyav_eABG{font-size:").concat(theme.h2FontSize || 'inherit', ";font-weight:").concat(theme.h2FontWeight || 'inherit', "}\n\n.emyav_dlZd{font-size:").concat(theme.h3FontSize || 'inherit', ";font-weight:").concat(theme.h3FontWeight || 'inherit', "}\n\n.emyav_bAmB{font-size:").concat(theme.h4FontSize || 'inherit', ";font-weight:").concat(theme.h4FontWeight || 'inherit', "}\n\n.emyav_eRZv{font-size:").concat(theme.h5FontSize || 'inherit', ";font-weight:").concat(theme.h5FontWeight || 'inherit', "}\n\n.emyav_dTMd{border-top:").concat(theme.borderWidth || 'inherit', " ").concat(theme.borderStyle || 'inherit', " ").concat(theme.borderColor || 'inherit', ";padding-top:").concat(theme.borderPadding || 'inherit', "}\n\n.emyav_evMo{border-bottom:").concat(theme.borderWidth || 'inherit', " ").concat(theme.borderStyle || 'inherit', " ").concat(theme.borderColor || 'inherit', ";padding-bottom:").concat(theme.borderPadding || 'inherit', "}\n\n.emyav_fAVi{color:inherit}\n\n.emyav_qFsi{color:").concat(theme.primaryColor || 'inherit', "}\n\n.emyav_bLsb{color:").concat(theme.secondaryColor || 'inherit', "}\n\n.emyav_ezBQ{color:").concat(theme.primaryInverseColor || 'inherit', "}\n\n.emyav_dlnd{color:").concat(theme.secondaryInverseColor || 'inherit', "}\n\n.emyav_ZpoW{font-size:inherit;font-weight:inherit;line-height:inherit;margin:0}\n\n.emyav_bOQC{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}");
  },
  'root': 'emyav_bGBk',
  'h1': 'emyav_fIqS',
  'h2': 'emyav_eABG',
  'h3': 'emyav_dlZd',
  'h4': 'emyav_bAmB',
  'h5': 'emyav_eRZv',
  'border-top': 'emyav_dTMd',
  'border-bottom': 'emyav_evMo',
  'color-inherit': 'emyav_fAVi',
  'color-primary': 'emyav_qFsi',
  'color-secondary': 'emyav_bLsb',
  'color-primary-inverse': 'emyav_ezBQ',
  'color-secondary-inverse': 'emyav_dlnd',
  'reset': 'emyav_ZpoW',
  'ellipsis': 'emyav_bOQC'
};
import theme from './theme';
/**
---
category: components/deprecated
id: DeprecatedHeading
---
**/

var Heading = (_dec = deprecated('7.0.0', null, 'Use Heading from ui-heading instead.'), _dec2 = testable(), _dec3 = themeable(theme, styles), _dec(_class = _dec2(_class = _dec3(_class = (_temp = _class2 =
/*#__PURE__*/
function (_Component) {
  _inherits(Heading, _Component);

  function Heading() {
    _classCallCheck(this, Heading);

    return _possibleConstructorReturn(this, _getPrototypeOf(Heading).apply(this, arguments));
  }

  _createClass(Heading, [{
    key: "render",
    value: function render() {
      var _classnames;

      var _this$props = this.props,
          border = _this$props.border,
          children = _this$props.children,
          color = _this$props.color,
          level = _this$props.level,
          ellipsis = _this$props.ellipsis,
          margin = _this$props.margin,
          elementRef = _this$props.elementRef;
      var ElementType = getElementType(Heading, this.props, function () {
        if (level === 'reset') {
          return 'span';
        } else {
          return level;
        }
      });
      var passthroughProps = View.omitViewProps(omitProps(this.props, Heading.propTypes), Heading);
      return React.createElement(View, Object.assign({}, passthroughProps, {
        className: classnames((_classnames = {}, _defineProperty(_classnames, styles.root, true), _defineProperty(_classnames, styles[level], true), _defineProperty(_classnames, styles["color-".concat(color)], color), _defineProperty(_classnames, styles["border-".concat(border)], border !== 'none'), _defineProperty(_classnames, styles.ellipsis, ellipsis), _classnames)),
        as: ElementType,
        margin: margin,
        elementRef: elementRef
      }), children);
    }
  }]);

  Heading.displayName = "Heading";
  return Heading;
}(Component), _class2.propTypes = {
  border: PropTypes.oneOf(['none', 'top', 'bottom']),
  children: childrenOrValue,
  color: PropTypes.oneOf(['primary', 'secondary', 'primary-inverse', 'secondary-inverse', 'inherit']),
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'reset']),

  /**
  * the element type to render as (defaults to the level)
  */
  as: PropTypes.elementType,
  // eslint-disable-line react/require-default-props
  ellipsis: PropTypes.bool,

  /**
  * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
  * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
  * familiar CSS-like shorthand. For example: `margin="small auto large"`.
  */
  margin: ThemeablePropTypes.spacing,
  elementRef: PropTypes.func
}, _class2.defaultProps = {
  children: null,
  margin: void 0,
  elementRef: void 0,
  border: 'none',
  color: 'inherit',
  level: 'h2',
  ellipsis: false
}, _temp)) || _class) || _class) || _class);
export default Heading;
export { Heading };